import { FC, ReactNode, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { buildReturnToRedirect } from '../../lib/returnTo'
import { useAuthStore } from '../../stores/authStore'
import { useFeatureFlagStore } from '../../stores/featureFlagStore'
// Direct file import (not the ../Layout barrel) so pulling the skeleton doesn't
// drag AppLayout — which imports back into ../Auth — and create an import cycle.
import { AppShellSkeleton } from '../Layout/AppShellSkeleton'

/**
 * AuthGuard component props
 */
interface AuthGuardProps {
  /** Content to render if authenticated */
  children: ReactNode
  /** Redirect path if not authenticated */
  redirectTo?: string
}

/**
 * AuthGuard - Protects routes by requiring authentication
 *
 * @example
 * ```tsx
 * <Route path="/chat" element={
 *   <AuthGuard>
 *     <ChatInterface />
 *   </AuthGuard>
 * } />
 * ```
 */
export const AuthGuard: FC<AuthGuardProps> = ({
  children,
  redirectTo = '/'
}) => {
  const { user, isAuthenticated, isLoading: isChecking, loadUser } = useAuthStore()
  const location = useLocation()

  useEffect(() => {
    // Use the store's loadUser() which calls /api/me and caches the result,
    // avoiding duplicate fetches when AuthGuard and other components both need user data
    if (!user && isAuthenticated) {
      loadUser().then(() => {
        if (useAuthStore.getState().isAuthenticated) {
          useFeatureFlagStore.getState().loadFlags()
        }
      })
    } else if (isAuthenticated && user) {
      // User already loaded, just ensure feature flags are loaded
      useFeatureFlagStore.getState().loadFlags()
    }
  }, [user, isAuthenticated, loadUser])

  // While boot-time auth is still resolving, paint a content-free app-shell
  // skeleton instead of a full-screen spinner. This makes a returning user
  // perceive the app appearing instantly. Shown UNCONDITIONALLY (no session
  // hint) — a logged-out reload briefly shows this frame then redirects to
  // /auth, which leaks nothing because the skeleton has zero real content.
  if (isChecking) {
    return <AppShellSkeleton />
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search
    const target = buildReturnToRedirect(currentPath, redirectTo)
    return <Navigate to={target} replace />
  }

  // Render protected content
  return <>{children}</>
}
